/**
 * Quem PODE virar evento de marketing, e quem não pode.
 *
 * O funil que a gente mede é o do PERSONAL: ele é quem chega por anúncio, cria
 * conta e assina. Tudo o mais que passa pelo mesmo domínio (o aluno usando o
 * app, o cliente do personal comprando um produto dele) não é tráfego nosso, e
 * quando vira evento o estrago é duplo: polui o GA4 e alimenta o Meta com gente
 * que nunca vai assinar, porque não é ela que decide isso.
 *
 * O corte acontece em três lugares, e os três são necessários:
 *
 * 1. **Por rota**, aqui embaixo (páginas do personal, área logada, porta do
 *    aluno);
 * 2. **por quem está logado** (`ehAlunoNesteAparelho`), porque o aluno navega em
 *    rota pública também;
 * 3. **no disparo** (`pushEvento` e `setUserData`, em `tracking-service.ts`),
 *    que é a rede de segurança: numa SPA o GTM pode já ter carregado na tela de
 *    entrar, antes de existir login, e continuar de pé depois que um aluno
 *    entrou.
 */

/**
 * Rotas públicas que pertencem ao negócio do PERSONAL, não ao WellTrainer:
 * página pública/bio (`/p/[username]` e subrotas), venda de produto
 * (`/produto/[slug]`), link compartilhável de avaliação (`/a/[slug]`),
 * pagamento de produto/serviço (`/pagar/[slug]`), convite de aluno
 * (`/convite/[username]`) e conferência de documento por link
 * (`/kyc-review/[token]`).
 *
 * Nessas páginas NÃO carregamos os pixels do WellTrainer (GTM → GA4 / Clarity /
 * Meta Pixel / Google Ads / Consent Mode, e UTMify) nem disparamos `pagina_vista`.
 * O tráfego e as conversões são do personal, que tem o pixel DELE na própria
 * página (ex.: `fb_pixel_id` na página de produto). Sem este gate, o nosso
 * Meta Pixel divide o mesmo `window.fbq` com o do personal e os eventos dele
 * vazam para a nossa conta (e os nossos PageViews poluem o funil).
 */
const PREFIXOS_PERSONAL = [
  '/p/',
  '/produto/',
  '/a/',
  '/pagar/',
  '/convite/',
  /* Quem abre é um terceiro conferindo um documento por link: não é visitante
     nosso, não está em funil nenhum. */
  '/kyc-review/',
] as const

export function ehRotaExternaDoPersonal(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return PREFIXOS_PERSONAL.some((prefixo) => pathname.startsWith(prefixo))
}

/**
 * Rotas que só o ALUNO abre, mesmo deslogado.
 *
 * `/ativar-conta` é a porta do aluno convidado pelo personal: quem chega ali
 * veio de um e-mail de convite, e 100% do tráfego é de aluno. Ela vive fora de
 * `/dashboard/`, então nenhum dos outros gates a pegava: o PageView saía, e a
 * ativação ainda disparava um `cadastro_concluido`, ou seja o Meta recebia
 * "cadastro novo" toda vez que um aluno de um personal ativava a conta dele.
 */
const PREFIXOS_DO_ALUNO = ['/ativar-conta'] as const

export function ehRotaDoAluno(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return PREFIXOS_DO_ALUNO.some((prefixo) => pathname.startsWith(prefixo))
}

/**
 * Rotas logadas de USO DO PRODUTO (dashboard do personal e área do aluno).
 * Nelas o GTM não deve carregar: PageView/scroll de uso diário poluem o GA4
 * de marketing e alimentam o Meta Pixel com "visitantes" que já são clientes
 * (auditoria 2026-07-08: 18.741 de 18.763 page_views do dia vinham do app
 * logado; Meta recebia 54K PageViews/dia).
 *
 * EXCEÇÃO (allowlist): fluxo de assinatura/upgrade do personal, são páginas
 * de conversão onde begin_checkout / add_payment_info / assinatura_paga
 * precisam do GTM ativo.
 */
const PREFIXOS_APP_INTERNO = ['/dashboard/'] as const
const PREFIXOS_CONVERSAO_PERMITIDOS = [
  /* Só a assinatura.
     `/dashboard/personal/planos` estava nesta lista por causa do nome: quem a
     escreveu leu "planos" como o plano da ASSINATURA, e ela é a biblioteca de
     planos de TREINO. Como o casamento é por prefixo, a exceção liberava o GTM
     numa tela de uso diário do app logado, que é exatamente o que esta lista
     existe para impedir. O fluxo de conversão de verdade (escolher plano,
     pagamento, sucesso) mora todo em `/dashboard/personal/assinatura`, e a rota
     antiga virou ponte para Meus treinos. */
  '/dashboard/personal/assinatura',
] as const

export function ehRotaInternaDoApp(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  if (PREFIXOS_CONVERSAO_PERMITIDOS.some((prefixo) => pathname.startsWith(prefixo))) return false
  return PREFIXOS_APP_INTERNO.some((prefixo) => pathname.startsWith(prefixo))
}

/**
 * Este aparelho está com uma conta de ALUNO aberta?
 *
 * O gate por rota não alcança o aluno: ele entra por `/login`, recupera senha em
 * `/forgot-password`, abre o link que o personal mandou. Em todas elas o GTM
 * carregava e, no login, o app ainda publicava o `user_data` dele (e-mail e
 * telefone hasheados) para o enhanced match do Meta. Aluno não é lead nosso, e
 * os dados pessoais dele não têm por que chegar lá.
 *
 * O tipo vem do usuário guardado no aparelho (`welltrainer_user`), que é
 * gravado no login e apagado na saída. `TRAINEE` é como o app chama o aluno
 * (o servidor diz `ALUNO`); os dois entram, porque o mesmo objeto já teve as
 * duas escritas ao longo do tempo.
 *
 * Deslogado responde `false` de propósito: na porta não dá para saber quem é, e
 * fechar o funil inteiro por causa da dúvida apagaria a medição de quem chega
 * por anúncio.
 */
export function ehAlunoNesteAparelho(): boolean {
  try {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false
    const cru = localStorage.getItem('welltrainer_user')
    if (!cru) return false
    const tipo = JSON.parse(cru)?.type
    return tipo === 'TRAINEE' || tipo === 'ALUNO'
  } catch {
    return false
  }
}

/**
 * A resposta única: esta visita vira evento de marketing ou não?
 *
 * Junta os dois cortes que dependem de contexto (a rota e quem está logado). O
 * terceiro, no disparo, mora em `tracking-service.ts`, porque lá a pergunta é
 * feita evento a evento.
 */
export function rastreamentoBloqueado(pathname: string | null | undefined): boolean {
  return (
    ehRotaExternaDoPersonal(pathname) ||
    ehRotaInternaDoApp(pathname) ||
    ehRotaDoAluno(pathname) ||
    ehAlunoNesteAparelho()
  )
}
